import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaChartLine, FaGlobeAmericas, FaBalanceScale, FaServer, FaCode, FaRobot,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaClock,
    FaShieldAlt, FaBolt, FaDatabase, FaExchangeAlt, FaMicrochip
} from 'react-icons/fa';
import heroImage from '../../assets/Futuristic Concept of Stock Market and Forex Trading Graph.png';

const CapitalMarketsPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaBolt, title: 'High-Frequency Trading', desc: 'Ultra-low latency execution engines using FPGA/ASIC.', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80' },
        { icon: FaRobot, title: 'Algorithmic Trading', desc: 'AI-driven strategies for VWAP, TWAP, and arbitrage.', image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800&q=80' },
        { icon: FaShieldAlt, title: 'Risk Management', desc: 'Real-time VaR (Value at Risk) and stress testing.', image: 'https://images.unsplash.com/photo-1543286386-713df548e9cc?w=800&q=80' },
        { icon: FaDatabase, title: 'Alternative Data', desc: 'Ingesting social, satellite, and sentiment data streams.', image: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&q=80' },
        { icon: FaExchangeAlt, title: 'Exchange Connectivity', desc: 'Direct Market Access (DMA) to global liquidity venues.', image: 'https://images.unsplash.com/photo-1611974765270-ca1258634369?w=800&q=80' },
        { icon: FaBalanceScale, title: 'RegTech & Compliance', desc: 'Automated reporting for MiFID II, CAT, and Dodd-Frank.', image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80' }
    ];

    const platforms = [
        { name: 'KDB+', icon: FaDatabase, desc: 'Time-Series DB' },
        { name: 'FIX Protocol', icon: FaExchangeAlt, desc: 'Messaging Standard' },
        { name: 'C++', icon: FaCode, desc: 'Low Latency Core' },
        { name: 'CUDA', icon: FaMicrochip, desc: 'GPU Acceleration' },
        { name: 'Python', icon: FaRobot, desc: 'Quant Research' },
        { name: 'AWS', icon: FaServer, desc: 'Grid Computing' }
    ];

    // Horizontal Flow Steps (Trade Lifecycle)
    const flowSteps = [
        { title: 'Ingest', icon: FaDatabase, color: 'from-emerald-500 to-green-600' },
        { title: 'Analyze', icon: FaRobot, color: 'from-teal-400 to-emerald-500' },
        { title: 'Decision', icon: FaCode, color: 'from-blue-500 to-indigo-500' },
        { title: 'Execute', icon: FaBolt, color: 'from-yellow-400 to-orange-500' },
        { title: 'Clear', icon: FaCheckCircle, color: 'from-purple-500 to-pink-500' },
        { title: 'Report', icon: FaBalanceScale, color: 'from-gray-500 to-slate-600' }
    ];

    const faqs = [
        { q: 'How low is your latency?', a: 'Our optimized HFT stacks achieve single-digit microsecond wire-to-wire latency for co-located servers.' },
        { q: 'Do you support crypto?', a: 'Yes, we build institutional-grade connectivity for crypto exchanges (Binance, Coinbase) and DeFi smart contracts.' },
        { q: 'Can you handle tick data?', a: 'We specialize in KDB+ and other time-series databases to store and query billions of tick data points instantly.' },
        { q: 'What about risk checks?', a: 'We implement pre-trade risk checks in hardware (FPGA) to ensure safety without compromising speed.' },
        { q: 'Is this only for hedge funds?', a: 'No, we serve investment banks, prop shops, exchanges, and retail brokers needing robust infrastructure.' }
    ];

    const whyChooseUs = [
        { title: 'Speed Obsessed', desc: 'We optimize every CPU cycle and network packet.' },
        { title: 'Quant Native', desc: 'Our engineers speak the language of alpha and beta.' },
        { title: 'Resilient', desc: 'Systems designed to handle "Flash Crash" volatility.' },
        { title: 'Global Reach', desc: 'Connectivity to 50+ exchanges worldwide.' },
        { title: 'Full Stack', desc: 'From kernel tuning to front-end trader dashboards.' },
        { title: 'Compliance Ready', desc: 'Audit trails built into every order lifecycle.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-green-900/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-emerald-300 mb-4">Industries &gt; Financial Services</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Capital <span className="text-emerald-400 font-normal">Markets</span></h1>
                        <p className="text-lg font-light text-emerald-100 leading-relaxed mb-8 max-w-2xl">
                            Powering the next generation of trading, clearing, and risk management with speed and precision.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-emerald-600 text-white font-medium rounded-sm hover:bg-emerald-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Upgrade Infrastructure
                            </a>
                            <a href="#services" className="px-8 py-3 border border-emerald-300 text-white font-medium rounded-sm hover:bg-white hover:text-emerald-900 transition-all text-sm uppercase tracking-wide">
                                Explore Tech
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">The Need for Speed</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            In markets, milliseconds mean millions.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Latency', val: '< 10µs' },
                            { label: 'Daily Volume', val: '$50Bn+' },
                            { label: 'Algorithms', val: '200+' },
                            { label: 'Market Access', val: 'Global' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-emerald-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Trading Solutions</h2>
                        <p className="text-lg text-slate-600">
                            Technology that gives you the edge.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-green-900/90 via-black/20 to-transparent"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-emerald-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-emerald-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-emerald-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Low Latency Stack</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {platforms.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-emerald-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Trade Lifecycle</h2>
                        <p className="text-slate-600">From signal to settlement.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-emerald-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-emerald-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Alpha <br />Generated</h2>
                            <p className="text-emerald-100 text-lg leading-relaxed mb-8">
                                We combine deep financial domain knowledge with Silicon Valley engineering to build trading systems that win.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-emerald-400 font-medium hover:text-white transition-colors">
                                Talk to a Quant Dev <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-emerald-800/50 p-6 rounded-sm border border-emerald-700 hover:border-green-500/50 transition-colors">
                                    <FaCheckCircle className="text-green-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-emerald-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Trading Tech FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-emerald-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-emerald-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Build Your Edge</h2>
                            <p className="text-slate-500 mb-8">Deploy strategy faster than the market.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="John Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="Hedge Fund X" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="john@fund.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Requirement</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors text-slate-700">
                                        <option>HFT Infrastructure</option>
                                        <option>Algo Development</option>
                                        <option>Data Engineering</option>
                                        <option>Risk Systems</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-emerald-600 text-white font-bold py-4 rounded-sm hover:bg-emerald-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Request Consultation
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CapitalMarketsPage;
